import { useCallback, useMemo } from 'react'
import { type GetProductMeshQueryVariables } from 'domains/products/api'
import { useGetProductDetailsMesh } from 'domains/products/api/getProductDetailsMesh.hooks'
import { useGetProductMesh } from 'domains/products/api/getProductMesh.hooks'
import { type ProductWithDetails } from 'domains/products/components/view/utils/types'
import { ProductTranslationNotFoundError } from 'domains/products/components/view/utils/errors'

export const useGetProduct = (variables: GetProductMeshQueryVariables) => {
  const {
    product,
    apiLoading: productLoading,
    apiError: productError,
    apiRefetch: productRefetch,
  } = useGetProductMesh(variables)

  const {
    productDetails,
    apiLoading: productDetailsLoading,
    apiError: productDetailsError,
    apiRefetch: productDetailsRefetch,
  } = useGetProductDetailsMesh({
    retailerId: variables.retailerId,
    productId: variables.productId,
  })

  const mergedProduct: ProductWithDetails | false = product && {
    ...product,
    ...productDetails,
  }

  /**
   * This is currently the only way to determine if the product does not have a translation.
   * Since the result of this hook is a combination between product details and product,
   * if we have one but not the other, we can assume the product exists, but not for a specific
   * locale.
   */
  const translationNotFoundError =
    productDetails && !product && !productError && !productLoading
      ? new ProductTranslationNotFoundError()
      : null

  const refetch = useCallback(() => {
    productRefetch()
    productDetailsRefetch()
  }, [productRefetch, productDetailsRefetch])

  return {
    product: mergedProduct || null,
    loading: productLoading || productDetailsLoading,
    error: productError || productDetailsError || translationNotFoundError,
    refetch,
  }
}
